import { useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { MuscleGroupVolume } from "@/lib/volume-balance";

interface MuscleHeatmapProps {
  muscleGroups: MuscleGroupVolume[];
}

// Map muscle groups to body regions for the heatmap
const BODY_REGIONS: Record<string, { x: number; y: number; width: number; height: number; label: string }> = {
  // Front view
  chest: { x: 35, y: 22, width: 30, height: 12, label: "Chest" },
  shoulders: { x: 25, y: 18, width: 50, height: 6, label: "Shoulders" },
  biceps: { x: 18, y: 28, width: 12, height: 14, label: "Biceps" },
  forearms: { x: 14, y: 44, width: 10, height: 12, label: "Forearms" },
  core: { x: 38, y: 36, width: 24, height: 14, label: "Core" },
  abs: { x: 40, y: 36, width: 20, height: 14, label: "Abs" },
  quads: { x: 32, y: 55, width: 36, height: 18, label: "Quads" },
  
  // Back view (offset to right side)
  back: { x: 135, y: 22, width: 30, height: 16, label: "Back" },
  lats: { x: 130, y: 28, width: 40, height: 12, label: "Lats" },
  traps: { x: 140, y: 12, width: 20, height: 8, label: "Traps" },
  triceps: { x: 118, y: 28, width: 12, height: 14, label: "Triceps" },
  "rear delts": { x: 125, y: 18, width: 50, height: 6, label: "Rear Delts" },
  glutes: { x: 135, y: 42, width: 30, height: 12, label: "Glutes" },
  hamstrings: { x: 132, y: 55, width: 36, height: 16, label: "Hamstrings" },
  calves: { x: 134, y: 75, width: 32, height: 12, label: "Calves" },
};

// Color scale from cold (undertrained) to hot (well-trained)
function getHeatColor(percentage: number): string {
  if (percentage === 0) return "hsl(var(--muted))"; // Gray for untrained
  if (percentage < 5) return "hsl(210, 60%, 75%)"; // Light blue
  if (percentage < 10) return "hsl(180, 60%, 60%)"; // Cyan
  if (percentage < 15) return "hsl(120, 50%, 55%)"; // Green
  if (percentage < 20) return "hsl(60, 70%, 50%)"; // Yellow
  if (percentage < 25) return "hsl(30, 80%, 55%)"; // Orange
  return "hsl(0, 70%, 55%)"; // Red for heavily trained
}

function getIntensityLabel(percentage: number): string {
  if (percentage === 0) return "Not trained";
  if (percentage < 5) return "Low";
  if (percentage < 10) return "Moderate";
  if (percentage < 15) return "Good";
  if (percentage < 20) return "High";
  return "Very High";
}

export function MuscleHeatmap({ muscleGroups }: MuscleHeatmapProps) {
  const muscleMap = useMemo(() => {
    const map = new Map<string, MuscleGroupVolume>();
    muscleGroups.forEach((mg) => {
      map.set(mg.muscleGroup.toLowerCase(), mg);
    });
    return map;
  }, [muscleGroups]);

  const getMuscleData = (muscleName: string) => {
    const data = muscleMap.get(muscleName.toLowerCase());
    return {
      sets: data?.sets || 0,
      percentage: data?.percentage || 0,
      exercises: data?.exercises || [],
    };
  };

  if (muscleGroups.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Muscle Heatmap</CardTitle>
          <CardDescription>Training frequency by body part</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="chart-height-lg flex items-center justify-center text-muted-foreground">
            Complete some workouts to see your muscle heatmap
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Muscle Heatmap</CardTitle>
        <CardDescription>Training frequency by body part</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col md:flex-row gap-4">
          {/* Body diagram */}
          <div className="flex-1">
            <svg viewBox="0 0 200 100" className="w-full h-auto max-h-[320px]">
              {/* Front body outline */}
              <g className="front-body">
                <text x="50" y="8" textAnchor="middle" className="text-[4px] fill-muted-foreground font-medium">FRONT</text>
                {/* Head */}
                <ellipse cx="50" cy="8" rx="6" ry="6" className="fill-muted/30 stroke-border" strokeWidth="0.5" />
                {/* Neck */}
                <rect x="47" y="12" width="6" height="4" className="fill-muted/30 stroke-border" strokeWidth="0.5" />
                {/* Torso */}
                <path d="M30 16 L70 16 L72 50 L28 50 Z" className="fill-muted/20 stroke-border" strokeWidth="0.5" />
                {/* Arms */}
                <path d="M30 16 L20 18 L12 55 L18 56 L25 30 L28 50" className="fill-muted/20 stroke-border" strokeWidth="0.5" />
                <path d="M70 16 L80 18 L88 55 L82 56 L75 30 L72 50" className="fill-muted/20 stroke-border" strokeWidth="0.5" />
                {/* Legs */}
                <path d="M28 50 L30 52 L32 90 L48 90 L50 52" className="fill-muted/20 stroke-border" strokeWidth="0.5" />
                <path d="M72 50 L70 52 L68 90 L52 90 L50 52" className="fill-muted/20 stroke-border" strokeWidth="0.5" />
              </g>

              {/* Back body outline */}
              <g className="back-body">
                <text x="150" y="8" textAnchor="middle" className="text-[4px] fill-muted-foreground font-medium">BACK</text>
                {/* Head */}
                <ellipse cx="150" cy="8" rx="6" ry="6" className="fill-muted/30 stroke-border" strokeWidth="0.5" />
                {/* Neck */}
                <rect x="147" y="12" width="6" height="4" className="fill-muted/30 stroke-border" strokeWidth="0.5" />
                {/* Torso */}
                <path d="M130 16 L170 16 L172 50 L128 50 Z" className="fill-muted/20 stroke-border" strokeWidth="0.5" />
                {/* Arms */}
                <path d="M130 16 L120 18 L112 55 L118 56 L125 30 L128 50" className="fill-muted/20 stroke-border" strokeWidth="0.5" />
                <path d="M170 16 L180 18 L188 55 L182 56 L175 30 L172 50" className="fill-muted/20 stroke-border" strokeWidth="0.5" />
                {/* Legs */}
                <path d="M128 50 L130 52 L132 90 L148 90 L150 52" className="fill-muted/20 stroke-border" strokeWidth="0.5" />
                <path d="M172 50 L170 52 L168 90 L152 90 L150 52" className="fill-muted/20 stroke-border" strokeWidth="0.5" />
              </g>

              {/* Muscle group overlays - Front */}
              {/* Chest */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <rect
                    x="35" y="20" width="30" height="10"
                    rx="2"
                    fill={getHeatColor(getMuscleData("chest").percentage)}
                    className="cursor-pointer hover:opacity-80 transition-opacity"
                    opacity="0.85"
                  />
                </TooltipTrigger>
                <TooltipContent>
                  <MuscleTooltip name="Chest" data={getMuscleData("chest")} />
                </TooltipContent>
              </Tooltip>

              {/* Shoulders (front) */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <g className="cursor-pointer hover:opacity-80 transition-opacity">
                    <ellipse cx="28" cy="19" rx="5" ry="4" fill={getHeatColor(getMuscleData("shoulders").percentage)} opacity="0.85" />
                    <ellipse cx="72" cy="19" rx="5" ry="4" fill={getHeatColor(getMuscleData("shoulders").percentage)} opacity="0.85" />
                  </g>
                </TooltipTrigger>
                <TooltipContent>
                  <MuscleTooltip name="Shoulders" data={getMuscleData("shoulders")} />
                </TooltipContent>
              </Tooltip>

              {/* Biceps */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <g className="cursor-pointer hover:opacity-80 transition-opacity">
                    <ellipse cx="22" cy="32" rx="4" ry="8" fill={getHeatColor(getMuscleData("biceps").percentage)} opacity="0.85" />
                    <ellipse cx="78" cy="32" rx="4" ry="8" fill={getHeatColor(getMuscleData("biceps").percentage)} opacity="0.85" />
                  </g>
                </TooltipTrigger>
                <TooltipContent>
                  <MuscleTooltip name="Biceps" data={getMuscleData("biceps")} />
                </TooltipContent>
              </Tooltip>

              {/* Core/Abs */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <rect
                    x="40" y="32" width="20" height="16"
                    rx="2"
                    fill={getHeatColor(getMuscleData("core").percentage || getMuscleData("abs").percentage)}
                    className="cursor-pointer hover:opacity-80 transition-opacity"
                    opacity="0.85"
                  />
                </TooltipTrigger>
                <TooltipContent>
                  <MuscleTooltip name="Core" data={getMuscleData("core").sets > 0 ? getMuscleData("core") : getMuscleData("abs")} />
                </TooltipContent>
              </Tooltip>

              {/* Quads */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <g className="cursor-pointer hover:opacity-80 transition-opacity">
                    <rect x="32" y="52" width="14" height="22" rx="3" fill={getHeatColor(getMuscleData("quads").percentage)} opacity="0.85" />
                    <rect x="54" y="52" width="14" height="22" rx="3" fill={getHeatColor(getMuscleData("quads").percentage)} opacity="0.85" />
                  </g>
                </TooltipTrigger>
                <TooltipContent>
                  <MuscleTooltip name="Quads" data={getMuscleData("quads")} />
                </TooltipContent>
              </Tooltip>

              {/* Muscle group overlays - Back */}
              {/* Back/Lats */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <rect
                    x="135" y="18" width="30" height="20"
                    rx="2"
                    fill={getHeatColor(Math.max(getMuscleData("back").percentage, getMuscleData("lats").percentage))}
                    className="cursor-pointer hover:opacity-80 transition-opacity"
                    opacity="0.85"
                  />
                </TooltipTrigger>
                <TooltipContent>
                  <MuscleTooltip name="Back" data={getMuscleData("back").sets > getMuscleData("lats").sets ? getMuscleData("back") : getMuscleData("lats")} />
                </TooltipContent>
              </Tooltip>

              {/* Traps */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <path
                    d="M140 14 L160 14 L155 18 L145 18 Z"
                    fill={getHeatColor(getMuscleData("traps").percentage)}
                    className="cursor-pointer hover:opacity-80 transition-opacity"
                    opacity="0.85"
                  />
                </TooltipTrigger>
                <TooltipContent>
                  <MuscleTooltip name="Traps" data={getMuscleData("traps")} />
                </TooltipContent>
              </Tooltip>

              {/* Triceps */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <g className="cursor-pointer hover:opacity-80 transition-opacity">
                    <ellipse cx="122" cy="32" rx="4" ry="8" fill={getHeatColor(getMuscleData("triceps").percentage)} opacity="0.85" />
                    <ellipse cx="178" cy="32" rx="4" ry="8" fill={getHeatColor(getMuscleData("triceps").percentage)} opacity="0.85" />
                  </g>
                </TooltipTrigger>
                <TooltipContent>
                  <MuscleTooltip name="Triceps" data={getMuscleData("triceps")} />
                </TooltipContent>
              </Tooltip>

              {/* Glutes */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <rect
                    x="135" y="40" width="30" height="10"
                    rx="3"
                    fill={getHeatColor(getMuscleData("glutes").percentage)}
                    className="cursor-pointer hover:opacity-80 transition-opacity"
                    opacity="0.85"
                  />
                </TooltipTrigger>
                <TooltipContent>
                  <MuscleTooltip name="Glutes" data={getMuscleData("glutes")} />
                </TooltipContent>
              </Tooltip>

              {/* Hamstrings */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <g className="cursor-pointer hover:opacity-80 transition-opacity">
                    <rect x="132" y="52" width="14" height="20" rx="3" fill={getHeatColor(getMuscleData("hamstrings").percentage)} opacity="0.85" />
                    <rect x="154" y="52" width="14" height="20" rx="3" fill={getHeatColor(getMuscleData("hamstrings").percentage)} opacity="0.85" />
                  </g>
                </TooltipTrigger>
                <TooltipContent>
                  <MuscleTooltip name="Hamstrings" data={getMuscleData("hamstrings")} />
                </TooltipContent>
              </Tooltip>

              {/* Calves */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <g className="cursor-pointer hover:opacity-80 transition-opacity">
                    <ellipse cx="140" cy="80" rx="5" ry="8" fill={getHeatColor(getMuscleData("calves").percentage)} opacity="0.85" />
                    <ellipse cx="160" cy="80" rx="5" ry="8" fill={getHeatColor(getMuscleData("calves").percentage)} opacity="0.85" />
                  </g>
                </TooltipTrigger>
                <TooltipContent>
                  <MuscleTooltip name="Calves" data={getMuscleData("calves")} />
                </TooltipContent>
              </Tooltip>
            </svg>
          </div>

          {/* Legend */}
          <div className="flex md:flex-col gap-2 justify-center flex-wrap">
            <div className="text-xs text-muted-foreground mb-1 hidden md:block">Intensity</div>
            {[
              { color: "hsl(0, 70%, 55%)", label: "Very High" },
              { color: "hsl(30, 80%, 55%)", label: "High" },
              { color: "hsl(60, 70%, 50%)", label: "Good" },
              { color: "hsl(120, 50%, 55%)", label: "Moderate" },
              { color: "hsl(180, 60%, 60%)", label: "Low" },
              { color: "hsl(var(--muted))", label: "None" },
            ].map(({ color, label }) => (
              <div key={label} className="flex items-center gap-1.5">
                <div
                  className="w-3 h-3 rounded-sm"
                  style={{ backgroundColor: color }}
                />
                <span className="text-xs text-muted-foreground">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function MuscleTooltip({ name, data }: { name: string; data: { sets: number; percentage: number; exercises: string[] } }) {
  return (
    <div className="text-sm">
      <p className="font-medium">{name}</p>
      <p className="text-muted-foreground">
        {data.sets} sets ({data.percentage.toFixed(1)}%)
      </p>
      <p className="text-muted-foreground">
        Intensity: {getIntensityLabel(data.percentage)}
      </p>
      {data.exercises.length > 0 && (
        <p className="text-xs text-muted-foreground mt-1">
          {data.exercises.slice(0, 3).join(", ")}
          {data.exercises.length > 3 && ` +${data.exercises.length - 3} more`}
        </p>
      )}
    </div>
  );
}
